import type { ForecastPreviewDataPointDTO, ForecastPreviewResponseDto } from '@/src/api/types/procurement'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import type { LoadPlanningAdjustmentFormValidated } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import {
  getDateRangeOfYear,
  getEnergyUnitType,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { appColors } from '@/src/theme'

const previewColorTable = {
  actualConsumptionValue: appColors.accentPrimary,
  draftValue: appColors.secondaryForeground,
  previewValue: appColors.primary,
}

export type LoadPlanningPreviewCurveDataPointValueProperties = Pick<ForecastPreviewDataPointDTO, 'draftValue' | 'previewValue'>

export const getPreviewColor = (dataKey: keyof LoadPlanningPreviewCurveDataPointValueProperties) => {
  return previewColorTable[dataKey]
}

export const getPreviewDataByGranularity = (previewData: ForecastPreviewResponseDto, granularity: GranularityEnum) => {
  switch (granularity) {
    case GranularityEnum.FIFTEEN_MINUTES:
      return previewData.quarterHourlyData ?? []
    case GranularityEnum.ONE_HOUR:
      return previewData.hourlyData ?? []
    case GranularityEnum.ONE_MONTH:
      return previewData.monthlyData ?? []
    default:
      return previewData.monthlyData ?? []
  }
}

export const getPeakPointValue = (dataSource: ForecastPreviewDataPointDTO[], granularity: GranularityEnum): number => {
  const unitProperty = getEnergyUnitType(granularity)
  return dataSource.reduce((prev, tick) => {
    const draftAmount = tick.draftValue?.[unitProperty]?.amount ?? 0
    const previewAmount = tick.previewValue?.[unitProperty]?.amount ?? 0
    const actualAmount = tick.actualConsumptionValue?.[unitProperty]?.amount ?? 0

    if (draftAmount > prev) return draftAmount
    if (previewAmount > prev) return previewAmount
    if (actualAmount > prev) return actualAmount
    return prev
  }, 0)
}

export const getFifteenMinutesDefaultTimeRange = (dataToPreview?: LoadPlanningAdjustmentFormValidated, year?: number): [Date, Date] => {
  if (dataToPreview?.timeframeStart && dataToPreview?.timeframeEnd) {
    return [new Date(dataToPreview.timeframeStart), new Date(dataToPreview.timeframeEnd)]
  }

  if (year) {
    const { minDate, maxDate } = getDateRangeOfYear(year)
    return [minDate!, maxDate!]
  }

  return [new Date(), new Date()]
}
